/**
 * Canonical ambito table — single source of truth.
 *
 * IDs are anchored to the production Railway DB (which inherited the legacy
 * MySQL IDs for INS/MET/EBTC/ELM and added RSO at the previously-unused id=1).
 * Do NOT reorder or renumber: `ot.ambitoId`, `plan_matriz.ambitoId`, and
 * `usuario_x_ambito.ambito_id` reference these by ID.
 *
 * The 2026-04-30 incident (pcastro/emilio reportedly seeing RSO instead of
 * their real ambito) was caused by four duplicated copies of this table
 * across `seed.ts`, `apply-roster-update.ts`, `inspect-roster-xlsx.ts`, and
 * `use-ambitos.ts` AMBITOS_FALLBACK drifting from the DB. Consolidating
 * here lets a single edit propagate; the lock-in test in
 * `__tests__/ambitos-canonical.test.ts` fails if anyone tries to renumber.
 *
 * The string "Residuos Solidos" without accents is intentional — it matches
 * a row that exists in production with `nombre = "rso"` (lowercase) created
 * pre-seed. The seed upsert keeps the row alive without renaming so existing
 * FKs continue to resolve.
 */

export interface AmbitoCanonical {
  readonly id: number;
  readonly codigo: string;
  readonly nombre: string;
  readonly subcontratoId: number;
}

export const AMBITO_CANONICAL: ReadonlyArray<AmbitoCanonical> = [
  { id: 1, codigo: 'RSO',  nombre: 'Residuos Solidos',                   subcontratoId: 2 },
  { id: 2, codigo: 'INS',  nombre: 'Instrumentacion',                    subcontratoId: 1 },
  { id: 3, codigo: 'MET',  nombre: 'Metrologia',                         subcontratoId: 1 },
  { id: 4, codigo: 'EBTC', nombre: 'Electricidad Baja Tension y Control', subcontratoId: 2 },
  { id: 5, codigo: 'ELM',  nombre: 'Electrico Lineas y Media Tension',    subcontratoId: 2 },
] as const;

/**
 * Quick id→codigo lookup. Built once at module load; do not mutate.
 */
export const AMBITO_CODIGO_BY_ID: Readonly<Record<number, string>> = Object.freeze(
  Object.fromEntries(AMBITO_CANONICAL.map((a) => [a.id, a.codigo])),
);

/**
 * Quick codigo→id lookup for parsing roster XLSX columns and similar.
 */
export const AMBITO_ID_BY_CODIGO: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(AMBITO_CANONICAL.map((a) => [a.codigo, a.id])),
);

/**
 * Ámbitos that expose the manual "Nuevo Ticket" NPL/PROGRAMADO toggle.
 *
 * Instrumentacion (INS=2) and Baja Tension (EBTC=4) both carry an ECO catalog,
 * so a manually-created PROGRAMADO ticket can synthesize a plan_matriz (eco +
 * pauta + glosa) for either. Every other ámbito stays NPL-only in the manual
 * form. Single source of truth consumed by the web form (CreateTicketModal),
 * the mantenedor route superRefine, and the mantenedor service synthesis gate —
 * keep these three in lockstep by importing this constant rather than hardcoding.
 */
export const PROGRAMADO_AMBITO_IDS: readonly number[] = AMBITO_CANONICAL.filter(
  (a) => a.codigo === 'INS' || a.codigo === 'EBTC',
).map((a) => a.id);

/**
 * Ámbitos whose NPL tickets carry the ELECTRICO/AIRE equipment-subtype selector.
 *
 * Only Baja Tensión (EBTC=4): its NPL correlativos split into eléctrico
 * (`EBTC-9<MMYY><seq>`) and aire (`EBTC-8<MMYY><seq>`). Instrumentación NPL uses
 * a global running counter with no such split, and every other ámbito stays on
 * the ámbito-agnostic `MMYY-NNNN` scheme. Single source of truth consumed by the
 * web form (CreateTicketModal), the mantenedor route superRefine, and the
 * service validation — import this rather than hardcoding the id, exactly as
 * PROGRAMADO_AMBITO_IDS is used.
 */
export const NPL_EQUIPO_SELECTOR_AMBITO_IDS: readonly number[] = AMBITO_CANONICAL.filter(
  (a) => a.codigo === 'EBTC',
).map((a) => a.id);

/**
 * Ámbitos whose manually-created (mantenedor) tickets continue the LEGACY production
 * numbering (`PREFIX-<MMYY><seq>`) instead of the ámbito-agnostic `MMYY-NNNN` scheme:
 * Instrumentación (INS) and Baja Tensión (EBTC) — the only ámbitos with a legacy
 * correlativo family to continue.
 *
 * Kept SEPARATE from PROGRAMADO_AMBITO_IDS on purpose: today the two sets coincide,
 * but "carries an ECO/PROGRAMADO catalog" and "uses the legacy correlativo family"
 * are distinct concepts. Decoupling them means a future 3rd PROGRAMADO ámbito won't
 * silently inherit a legacy numbering scheme it has no prod series to continue.
 */
export const LEGACY_CORRELATIVO_AMBITO_IDS: readonly number[] = AMBITO_CANONICAL.filter(
  (a) => a.codigo === 'INS' || a.codigo === 'EBTC',
).map((a) => a.id);

/**
 * Curated ubicación allowlist per ámbito for the manual "Nuevo Ticket" form.
 *
 * The `ubicacion` catalog is FLAT (no ambitoId column — see prisma `model Ubicacion`).
 * `GET /catalogos/ubicaciones?ambitoId=X` therefore derives an ámbito's locations from
 * HISTORICAL usage (`ot.ubicacionId` ∪ `plan_matriz.ubicacionId`). For Baja Tensión
 * (EBTC=4) that history surfaces 32 rows full of legacy duplicates (CALETONES ×2,
 * COLON BAJO ×4, COLON ALTO ×3, SEWELL / S/E SEWELL / SEWELL CANCHA 6) and unrelated
 * sites (INTERIOR MINA, RAJO SUR, MUTUAL DE SEGURIDAD, PATIO RANCAGUA, SAG, JOACHIM…).
 * History is a poor proxy for "what the operator actually uses today", so we pin the
 * real El Teniente sites Pablo (BT) works with.
 *
 * IDs are anchored to the production Railway `ubicacion` table (BT verified 2026-06-02,
 * INS 2026-09-23).
 * CALETONES=678 was chosen over its duplicate row 673 (678 carries 945 BT OTs vs 23;
 * 673 is data-debt to be merged separately). `OTRA` is NOT a DB row — the web form
 * appends it as a sentinel (`tickets-mantenedor.tsx`), so it is intentionally absent.
 *
 * Instrumentación (INS=2) is curated from Emilio's sheet "lugares CREAR TICKETS
 * rev0.1" (2026-09-23): 92 sites replacing a 481-row historical list. Where a name
 * hits two rows, the pick is the row INS already uses most — so INS and BT can
 * point at different rows of the same duplicated site (LA JUNTA 567 vs 676,
 * MAITENES 572 vs 642) until the duplicates are merged.
 *
 * An ámbito WITHOUT an entry here falls back to the historical ot/plan_matriz
 * derivation (unchanged behavior). To curate another ámbito, add its entry below.
 * Consumed by `catalogosRoutes` (`GET /ubicaciones`); a lock-in test guards the IDs
 * and names each one.
 */
export const UBICACION_ALLOWLIST_BY_AMBITO: Readonly<Record<number, readonly number[]>> = Object.freeze({
  // Instrumentación (INS=2) — Emilio's curated sites
  2: Object.freeze([
    555, 556, 559, 560, 561, 562, 563, 564, 565, 566, 567, 568, 570, 571, 572, 573,
    574, 575, 576, 577, 578, 579, 583, 584, 585, 586, 587, 588, 589, 590, 594, 595,
    598, 601, 602, 605, 606, 607, 608, 611, 612, 613, 615, 616, 619, 620, 621, 624,
    625, 626, 627, 628, 629, 630, 631, 632, 633, 634, 635, 636, 637, 639, 640, 643,
    646, 647, 650, 653, 654, 656, 667, 669, 670, 677, 678, 679, 681, 682, 683, 688,
    689, 690, 691, 692, 693, 695, 696, 698, 699, 700, 701, 702,
  ]),
  // Baja Tensión (EBTC=4) — Pablo's curated El Teniente sites
  4: Object.freeze([555, 560, 561, 562, 568, 585, 642, 670, 676, 677, 678]),
});
